import { z } from "zod";
import { policySeveritySchema } from "./policies.schema";

const policyFindingFilterDataSchema = z.object({
  options: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
});

export const findingsFiltersSuccessResponseSchema = z.object({
  policy_name: policyFindingFilterDataSchema,
  closure_reason: policyFindingFilterDataSchema,
  source: policyFindingFilterDataSchema,
  status: policyFindingFilterDataSchema,
  category: policyFindingFilterDataSchema,
});

export type PolicyFindingFilterData = z.infer<
  typeof policyFindingFilterDataSchema
>;

export type FindingsFiltersSuccessResponse = z.infer<
  typeof findingsFiltersSuccessResponseSchema
>;

export const FindingsActionTypeSchema = z.enum(["DELETE", "DISABLEACCOUNT"]);
export type FindingsActionType = z.infer<typeof FindingsActionTypeSchema>;

export const PolicyFindingsTicketActionPayloadSchema = z.object({
  action: FindingsActionTypeSchema,
  assetId: z.string(),
  violationId: z.string(),
  businessJustification: z.string(),
  isCompliantJustification: z.boolean(),
});
export type PolicyFindingsTicketActionPayload = z.infer<
  typeof PolicyFindingsTicketActionPayloadSchema
>;

export const policyFindingsTicketActionResponseSchema = z.object({
  success: z.literal(false),
  error_code: z.string(),
  message: z.string(),
});
export type PolicyFindingsTicketActionResponse = z.infer<
  typeof policyFindingsTicketActionResponseSchema
>;

export const FindingsRequestPayloadSchema = z.object({
  status: z.array(z.string()).optional(),
  policy_id: z.string().optional(),
  closure_reason: z.array(z.string()).optional(),
  policy_name: z.array(z.string()).optional(),
  display_name: z.string().optional(),
  source_id: z.array(z.string()).optional(),
  category: z.array(z.string()).optional(),
  sortBy: z.string().optional(),
  sortOrder: z.string().optional(),
});

export type FindingsRequestPayload = z.infer<
  typeof FindingsRequestPayloadSchema
>;

const remediationDataSchema = z.object({
  remediationTarget: z.enum(["saviynt_eic", "servicenow"]),
  hyperlink: z.string(),
  identifier: z.string(),
  actionType: z.string(),
  externalStatus: z.enum(["open", "closed"]),
});

const policyFindingDetailSchema = z.object({
  identityResource: z.string(),
  nativeType: z.string(),
  createdTs: z.number(),
  id: z.string(),
  policyName: z.string(),
  triggerPolicyId: z.string().uuid(),
  identityName: z.string(),
  identityDisplayName: z.string(),
  nativeSource: z.string(),
  severity: policySeveritySchema,
  status: z.string(),
  tags: z.string(),
  ttr: z.number().nullable(),
  assetId: z.string(),
  violationId: z.string(),
  category: z.string(),
  remediationData: z.array(remediationDataSchema),
  allowedActions: z.array(z.string()),
  showDeleteDisableAccountEnableText: z.boolean(),
});

export const policyFindingsResponseSchema = z.object({
  values: z.array(policyFindingDetailSchema),
  totalCount: z.number(),
  nextToken: z.number(),
});

export type PolicyFindingsResponse = z.infer<
  typeof policyFindingsResponseSchema
>;
export type PolicyFindingRemediationData = z.infer<
  typeof remediationDataSchema
>;
export type PolicyFindingDetail = z.infer<typeof policyFindingDetailSchema>;

const nodeDataSchema = z.object({
  id: z.string(),
  srn: z.string(),
  displayName: z.string(),
  label: z.string(),
  type: z.string(),
  source: z.string(),
  policyViolationCount: z.number(),
  isOpenToAllInternet: z.boolean(),
  policyViolationDetails: z.array(
    z.object({
      name: z.string(),
      policyId: z.string(),
      severity: z.string(),
    }),
  ),
  usesRelationship: z.boolean().optional(),
  onMakeItFocusedNodeClick: z
    .function()
    .args(z.unknown())
    .returns(z.void())
    .optional(),
  onNodeClick: z.function().args(z.any()).returns(z.void()),
  styles: z.record(z.unknown()).optional(),
  isRoot: z.boolean().optional(),
  ariaLabel: z.string().optional(),
  isLeftSectionExist: z.boolean().optional(),
  isRightSectionExist: z.boolean().optional(),
});

const edgeDataSchema = z.object({
  id: z.string(),
  label: z.string(),
  source: z.string(),
  target: z.string(),
  relationshipType: z.string(),
});

export const accessMapResponseSchema = z.object({
  data: z.object({
    nodes: z.array(nodeDataSchema),
    edges: z.array(edgeDataSchema),
  }),
});

export type NodeData = z.infer<typeof nodeDataSchema>;
export type AccessMapResponse = z.infer<typeof accessMapResponseSchema>;
